import { useTranslation } from 'react-i18next';
import { useSettings, formatNumber } from '../context/SettingsContext';
import type { VatStatus } from '../types';

interface Props {
  label: string;
  amount: string;
  vatStatus: VatStatus;
  vatAmount: string;
  onAmountChange: (v: string) => void;
  onVatStatusChange: (v: VatStatus) => void;
  onVatAmountChange: (v: string) => void;
  vatRate: number;
  disabled?: boolean;
}

export default function PriceInput({
  label, amount, vatStatus, vatAmount,
  onAmountChange, onVatStatusChange, onVatAmountChange,
  vatRate, disabled = false,
}: Props) {
  const { t } = useTranslation();
  const { settings } = useSettings();

  // Parser dansk tusindetalsformat ("6.500,00") — uden replace tolker parseFloat
  // "6.500" som 6.5 og momsen bliver 1,3 i stedet for 1.300.
  const amountNum = parseFloat(amount.replace(/\./g, '').replace(',', '.'));
  const autoVat = amount && Number.isFinite(amountNum) && amountNum !== 0
    ? formatNumber(amountNum * vatRate / (100 + vatRate), settings.numberFormat)
    : '';

  const vatFieldValue = vatStatus === 'full' ? autoVat
    : vatStatus === 'exempt' ? '0'
    : vatAmount;

  const vatReadOnly = vatStatus !== 'partial';

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">{label}</label>
          <input
            type="text" inputMode="decimal" disabled={disabled}
            className="input"
            value={amount}
            onChange={e => onAmountChange(e.target.value)}
            placeholder="0,00"
          />
        </div>
        <div>
          <label className="label">{t('vat.amount_label')}</label>
          <input
            type="text" inputMode={vatReadOnly ? 'text' : 'decimal'}
            disabled={disabled || vatReadOnly}
            className={`input ${vatReadOnly ? 'input-readonly' : ''}`}
            value={vatFieldValue}
            onChange={e => !vatReadOnly && onVatAmountChange(e.target.value)}
            placeholder={vatStatus === 'partial' ? '0,00' : ''}
            readOnly={vatReadOnly}
          />
        </div>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        {(['full', 'partial', 'exempt'] as VatStatus[]).map(s => (
          <label key={s} className="checkbox-label">
            <input
              type="radio" name={`vat-${label}`} value={s}
              checked={vatStatus === s}
              onChange={() => onVatStatusChange(s)}
              disabled={disabled}
              className="text-[var(--color-primary)]"
            />
            {t(`vat.${s}`)}
          </label>
        ))}
      </div>
    </div>
  );
}
